import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Chip, Collapse, MenuItem,
  Dialog, DialogTitle, DialogContent, IconButton,
  Divider, InputAdornment
} from "@mui/material";

import LocationOnIcon    from "@mui/icons-material/LocationOn";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";
import FilterListIcon    from "@mui/icons-material/FilterList";
import CloseIcon         from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PaymentIcon       from "@mui/icons-material/Payment";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import TrendingDownIcon  from "@mui/icons-material/TrendingDown";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon           from "@mui/icons-material/Add";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";
import DownloadIcon      from "@mui/icons-material/Download";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";

// ✅ Detail modal — separate file
import EvProfitLossDetailModal from "../component/Evprofitlossdetailmodal";

// ✅ Export utilities — separate files (no inline export code in this file)
import { exportEvProfitLossExcel } from "../component/profitExcl";
import { exportEvProfitLossPDF   } from "../component/profitPdf";

import { profitLossService } from "../service/profitLossService";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const LOCATIONS     = ["Chaitanyapur", "Mahisadal"];
const VEHICLE_TYPES = ["E_RICKSHAW", "SCOOTY"];

const LOC_COLOR = {
  Chaitanyapur: { bg: "#f3e5f5", border: "#7b1fa2", badge: "#7b1fa2", light: "#e1bee7" },
  Mahisadal:    { bg: "#e8f5e9", border: "#2e7d32", badge: "#2e7d32", light: "#c8e6c9" },
  Other:        { bg: "#fff8e1", border: "#f57f17", badge: "#f57f17", light: "#ffecb3" },
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmt     = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const Label = ({ children, color = "#777" }) => (
  <Typography variant="caption"
    sx={{ color, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", fontSize: "0.62rem" }}>
    {children}
  </Typography>
);
const Val = ({ children, color }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, color: color || "#111", mt: 0.1 }}>
    {children ?? "—"}
  </Typography>
);

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
const ProfitLossDashboard = () => {

  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [summary,     setSummary]     = useState({});
  const [expandedLoc, setExpandedLoc] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  // ── detail modal ──────────────────────────────────────────────────────────
  const [detailOpen,    setDetailOpen]    = useState(false);
  const [detailData,    setDetailData]    = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── due payment modal ─────────────────────────────────────────────────────
  const [dueOpen,       setDueOpen]       = useState(false);
  const [dueSale,       setDueSale]       = useState(null);
  const [dueForm,       setDueForm]       = useState({
    amount: "",
    date:   new Date().toISOString().split("T")[0],
    note:   "",
    remark: "",   // ✅ evSaleSchema due.payments has remark field
  });
  const [dueSubmitting, setDueSubmitting] = useState(false);
  const [dueError,      setDueError]      = useState("");
  const [dueSuccess,    setDueSuccess]    = useState("");

  const [filters, setFilters] = useState({
    vehicleType: "",
    model:       "",
    location:    "",
    status:      "",
    fromDate:    "",
    toDate:      "",
    search:      "",
    mobile:      "",
  });

  // group by location
  const grouped = {};
  data.forEach((b) => {
    const loc = b.location || "Other";
    if (!grouped[loc]) grouped[loc] = [];
    grouped[loc].push(b);
  });

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (overrideFilters) => {
    try {
      setLoading(true);
      setError("");
      const res      = await profitLossService.getAllProfitLoss(overrideFilters ?? filters);
      const vehicles = res?.data || [];
      setData(vehicles);
      setSummary({
        grandProfitLoss:       res?.grandProfitLoss       ?? 0,
        grandStatus:           res?.grandStatus           ?? "PROFIT",
        grandTotalDue:         res?.grandTotalDue         ?? 0,
        totalDueVehicles:      res?.totalDueVehicles      ?? 0,
        grandTotalExchangeDue: res?.grandTotalExchangeDue ?? 0,
        totalExchangeVehicles: res?.totalExchangeVehicles ?? 0,
        totalVehicles:         res?.totalVehicles         ?? 0,
      });
      const exp = {};
      vehicles.forEach((b) => { if (b.location) exp[b.location] = true; });
      exp["Other"] = true;
      setExpandedLoc(exp);
    } catch {
      setError("Failed to load EV profit/loss data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleChange   = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  const resetFilters   = () => {
    const empty = {
      vehicleType: "", model: "", location: "",
      mobile: "", status: "", fromDate: "", toDate: "", search: ""
    };
    setFilters(empty);
    fetchData(empty);
  };
  const toggleLocation = (loc) => setExpandedLoc((p) => ({ ...p, [loc]: !p[loc] }));

  // ── view detail ────────────────────────────────────────────────────────────
  const handleView = async (id) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailData(null);
      const res = await profitLossService.getSpecificProfitLoss(id);
      setDetailData(res?.data || null);
    } catch {
      setError("Failed to load EV sale details");
    } finally {
      setDetailLoading(false);
    }
  };

  // ── due payment ────────────────────────────────────────────────────────────
  const openDueModal = (vehicle) => {
    setDueSale(vehicle);
    setDueForm({
      amount: "",
      date:   new Date().toISOString().split("T")[0],
      note:   "",
      remark: "",
    });
    setDueError("");
    setDueSuccess("");
    setDueOpen(true);
  };

  const handleDueSubmit = async () => {
    if (!dueForm.amount || Number(dueForm.amount) <= 0) {
      setDueError("Please enter a valid amount");
      return;
    }
    if (Number(dueForm.amount) > (dueSale?.due?.remainingAmount ?? 0)) {
      setDueError(`Max payable: ₹${fmt(dueSale?.due?.remainingAmount)}`);
      return;
    }
    try {
      setDueSubmitting(true);
      setDueError("");
      await profitLossService.addPayment(dueSale.saleId, {
        amount: Number(dueForm.amount),
        date:   dueForm.date,
        note:   dueForm.note,
        remark: dueForm.remark,   // ✅ evSaleSchema due.payments.remark
      });
      setDueSuccess("Payment recorded successfully!");
      setTimeout(() => { setDueOpen(false); fetchData(); }, 1200);
    } catch (err) {
      setDueError(err?.response?.data?.message || "Payment failed");
    } finally {
      setDueSubmitting(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── PAGE HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between"
        mb={2.5} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ bgcolor: "#0d1b2a", borderRadius: 2, p: 1, display: "flex" }}>
            <ElectricRickshawIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0d1b2a" letterSpacing={-0.5}>
              EV Profit &amp; Loss
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.totalVehicles ?? 0} vehicles · Showroom-wise breakdown
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          {/* ✅ exportEvProfitLossExcel imported from exportEvProfitLossExcel.js */}
          <Button
            variant="contained" color="success" size="small"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }}
            onClick={() => exportEvProfitLossExcel(data)}
          >
            Excel / CSV
          </Button>
          {/* ✅ exportEvProfitLossPDF imported from exportEvProfitLossPDF.js */}
          <Button
            variant="contained" color="error" size="small"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }}
            onClick={() => exportEvProfitLossPDF(data)}
          >
            PDF / HTML
          </Button>
          <Button
            startIcon={<FilterListIcon />}
            variant={filtersOpen ? "contained" : "outlined"}
            size="small"
            onClick={() => setFiltersOpen((p) => !p)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {filtersOpen ? "Hide Filters" : "Filters"}
          </Button>
        </Box>
      </Box>

      {/* ── SUMMARY CARDS ── */}
      {!loading && summary.totalVehicles > 0 && (
        <Grid container spacing={1.5} mb={2.5}>
          {[
            {
              label: "Grand P&L",
              value: `₹${fmt(summary.grandProfitLoss)}`,
              color: summary.grandStatus === "PROFIT" ? "#2e7d32" : "#c62828",
              bg:    summary.grandStatus === "PROFIT" ? "#e8f5e9" : "#ffebee",
              icon:  summary.grandStatus === "PROFIT"
                ? <TrendingUpIcon   sx={{ color: "#2e7d32" }} />
                : <TrendingDownIcon sx={{ color: "#c62828" }} />,
              chip: summary.grandStatus
            },
            {
              label: "Total Due Pending",
              value: `₹${fmt(summary.grandTotalDue)}`,
              color: summary.grandTotalDue > 0 ? "#e65100" : "#2e7d32",
              bg:    summary.grandTotalDue > 0 ? "#fff8f5" : "#e8f5e9",
              icon:  <WarningAmberIcon sx={{ color: summary.grandTotalDue > 0 ? "#e65100" : "#2e7d32" }} />,
              chip: `${summary.totalDueVehicles} vehicles`
            },
            {
              label: "Exchange Pending",
              value: `₹${fmt(summary.grandTotalExchangeDue)}`,
              color: summary.grandTotalExchangeDue > 0 ? "#1565c0" : "#2e7d32",
              bg:    summary.grandTotalExchangeDue > 0 ? "#e3f2fd" : "#e8f5e9",
              icon:  <SwapHorizIcon sx={{ color: summary.grandTotalExchangeDue > 0 ? "#1565c0" : "#2e7d32" }} />,
              chip: `${summary.totalExchangeVehicles} vehicles`
            },
            {
              label: "Total Vehicles",
              value: summary.totalVehicles,
              color: "#1565c0",
              bg:    "#e3f2fd",
              icon:  <ElectricRickshawIcon sx={{ color: "#1565c0" }} />,
              chip:  null
            }
          ].map(({ label, value, color, bg, icon, chip }) => (
            <Grid item xs={12} sm={3} key={label}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2.5, bgcolor: bg,
                border: `1.5px solid ${color}22`,
                display: "flex", alignItems: "center", gap: 1.5
              }}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, p: 0.8, display: "flex", boxShadow: 1 }}>
                  {icon}
                </Box>
                <Box flex={1}>
                  <Label color="#777">{label}</Label>
                  <Typography fontWeight={800} fontSize="1.05rem" color={color}>{value}</Typography>
                </Box>
                {chip && (
                  <Chip label={chip} size="small"
                    sx={{ bgcolor: color + "22", color, fontWeight: 700, fontSize: "0.65rem" }} />
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── FILTERS ── */}
      <Collapse in={filtersOpen}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid #e0e7ef", borderRadius: 3 }}>
          <Grid container spacing={1.5}>
            {/* ✅ vehicleType — EV-specific filter (E_RICKSHAW | SCOOTY) */}
            <Grid item xs={6} md={2}>
              <TextField select label="Vehicle Type" name="vehicleType" fullWidth size="small"
                value={filters.vehicleType} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {VEHICLE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.replace("_", " ")}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Model" name="model" fullWidth size="small"
                value={filters.model} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Location" name="location" fullWidth size="small"
                value={filters.location} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="P&L Status" name="status" fullWidth size="small"
                value={filters.status} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PROFIT">PROFIT</MenuItem>
                <MenuItem value="LOSS">LOSS</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Search" name="search" fullWidth size="small"
                value={filters.search} onChange={handleChange}
                placeholder="Invoice, Customer, Engine…" />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Mobile" name="mobile" fullWidth size="small"
                value={filters.mobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="From Date" name="fromDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="To Date" name="toDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Button fullWidth variant="contained" onClick={() => fetchData()}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#0d1b2a" }}>
                Apply
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button fullWidth variant="outlined" onClick={resetFilters}
                sx={{ borderRadius: 2, fontWeight: 700 }}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {loading  && <Box textAlign="center" py={6}><CircularProgress /></Box>}
      {error    && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && data.length === 0 && (
        <Alert severity="info">No EV data found for the selected filters.</Alert>
      )}

      {/* ── SHOWROOM SECTIONS ── */}
      {!loading && Object.entries(grouped).map(([loc, vehicles]) => {
        const theme  = LOC_COLOR[loc] || LOC_COLOR.Other;
        const isOpen = expandedLoc[loc] !== false;

        const locProfit      = vehicles.reduce((s, b) => s + (b.profitLoss                 ?? 0), 0);
        const locDue         = vehicles.reduce((s, b) => s + (b.due?.remainingAmount        ?? 0), 0);
        const locExchangeDue = vehicles.reduce((s, b) => s + (b.exchange?.remainingAmount   ?? 0), 0);
        const dueCount       = vehicles.filter((b) => b.hasDue).length;
        const exchangeCount  = vehicles.filter((b) => b.hasExchange).length;

        return (
          <Box key={loc} mb={3}>

            {/* ── LOCATION HEADER ── */}
            <Paper elevation={0} onClick={() => toggleLocation(loc)} sx={{
              px: 2.5, py: 1.5,
              background: `linear-gradient(135deg, ${theme.bg} 0%, #fff 100%)`,
              border: `2px solid ${theme.border}`,
              borderBottom: isOpen ? "none" : `2px solid ${theme.border}`,
              borderRadius: isOpen ? "12px 12px 0 0" : "12px",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 1
            }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <LocationOnIcon sx={{ color: theme.badge, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={800} color={theme.badge} letterSpacing={0.5}>
                  {loc}
                </Typography>
                <Chip label={`${vehicles.length} vehicles`} size="small"
                  sx={{ bgcolor: theme.badge, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {dueCount > 0 && (
                  <Chip icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                    label={`${dueCount} DUE · ₹${fmt(locDue)}`} size="small"
                    sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700, border: "1px solid #ffb74d", fontSize: "0.68rem" }}
                  />
                )}
                {exchangeCount > 0 && (
                  <Chip icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                    label={`${exchangeCount} EXCH · ₹${fmt(locExchangeDue)}`} size="small"
                    sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, border: "1px solid #90caf9", fontSize: "0.68rem" }}
                  />
                )}
                <Chip
                  icon={locProfit >= 0
                    ? <TrendingUpIcon   sx={{ fontSize: 14 }} />
                    : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                  label={`₹${fmt(locProfit)}`} size="small"
                  sx={{
                    bgcolor: locProfit >= 0 ? "#e8f5e9" : "#ffebee",
                    color:   locProfit >= 0 ? "#2e7d32" : "#c62828",
                    border:  `1px solid ${locProfit >= 0 ? "#a5d6a7" : "#ef9a9a"}`,
                    fontWeight: 700, fontSize: "0.7rem"
                  }}
                />
                {isOpen
                  ? <ExpandLessIcon sx={{ color: theme.badge }} />
                  : <ExpandMoreIcon sx={{ color: theme.badge }} />}
              </Box>
            </Paper>

            {/* ── TABLE ── */}
            <Collapse in={isOpen}>
              <Paper elevation={0} sx={{
                border: `2px solid ${theme.border}`, borderTop: "none",
                borderRadius: "0 0 12px 12px", overflow: "hidden"
              }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.light }}>
                        {[
                          "#", "Invoice", "Type", "Company / Model",
                          "Customer",
                          "Purchase Cost",   // totalPurchaseCost
                          "ExShowroom",      // salePrice
                          "ORP",             // orp — revenue base
                          "Sale Type",
                          "Expenses",        // expenses.total + charges
                          "Accessories",     // accessoryCost
                          "Due Remaining",   // due.remainingAmount
                          "Exch Remaining",  // exchange.remainingAmount
                          "Exch Net P/L",    // exchange.exchangeBikeNetPL
                          "Profit / Loss",   // profitLoss
                          "P&L Status",      // status
                          "Due Status",      // due.status
                          "Actions"
                        ].map((h) => (
                          <TableCell key={h} sx={{
                            fontWeight: 800, fontSize: "0.7rem", color: theme.badge,
                            letterSpacing: 0.4, whiteSpace: "nowrap", py: 1.2
                          }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles.map((b, idx) => {
                        const isProfit   = b.status === "PROFIT";
                        const isDue      = b.hasDue;
                        const isExchange = b.hasExchange;
                        // ✅ use exchangeBikeNetPL (from API) — not non-existent exchangeProfit/exchangeLossAmount
                        const exchNetPL  = b.exchange?.exchangeBikeNetPL ?? 0;

                        return (
                          <TableRow key={b.invoiceNo || idx}
                            sx={{
                              bgcolor: isDue ? "#fff8f5" : idx % 2 === 0 ? "#fff" : "#fafbff",
                              "&:hover": { bgcolor: isDue ? "#fff0eb" : "#f0f4ff" },
                              transition: "background 0.15s"
                            }}>

                            {/* # */}
                            <TableCell sx={{ color: "#aaa", fontSize: "0.72rem", fontWeight: 600 }}>
                              {idx + 1}
                            </TableCell>

                            {/* Invoice + Date */}
                            <TableCell sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1565c0", whiteSpace: "nowrap" }}>
                              {b.invoiceNo || "—"}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {fmtDate(b.saleDate)}
                              </Typography>
                            </TableCell>

                            {/* Vehicle Type — E_RICKSHAW | SCOOTY */}
                            <TableCell>
                              <Chip label={b.vehicleType?.replace("_", " ") || "—"} size="small"
                                sx={{
                                  fontWeight: 700, fontSize: "0.62rem",
                                  bgcolor: b.vehicleType === "E_RICKSHAW" ? "#e8f5e9" : "#e3f2fd",
                                  color:   b.vehicleType === "E_RICKSHAW" ? "#2e7d32" : "#1565c0",
                                }}
                              />
                            </TableCell>

                            {/* Company + Model */}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} fontSize="0.75rem">
                                {b.model || "—"}
                              </Typography>
                              {b.companyName && (
                                <Typography variant="caption" color="text.secondary">
                                  {b.companyName}
                                </Typography>
                              )}
                            </TableCell>

                            {/* Customer + Mobile */}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} fontSize="0.75rem">
                                {b.customerName || "—"}
                              </Typography>
                              {b.mobile && (
                                <Typography variant="caption" color="text.secondary">
                                  {/* ✅ mobile is array in EV model */}
                                  {Array.isArray(b.mobile) ? b.mobile[0] : b.mobile}
                                </Typography>
                              )}
                            </TableCell>

                            {/* Total Purchase Cost (purchasePrice + transportCost) */}
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                              ₹{fmt(b.totalPurchaseCost)}
                             
                             </TableCell>

                            {/* Sale Price (ExShowroom) */}
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                              ₹{fmt(b.salePrice)}
                            </TableCell>

                            {/* ORP — revenue base for P&L */}
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", color: "#1565c0" }}>
                              ₹{fmt(b.orp)}
                            </TableCell>

                            {/* Sale Type */}
                            <TableCell>
                              {b.saleType ? (
                                <Chip label={b.saleType} size="small"
                                  sx={{ fontWeight: 700, fontSize: "0.62rem", bgcolor: "#e3f2fd", color: "#1565c0" }} />
                              ) : "—"}
                            </TableCell>

                            {/* Expenses total + RTO/Insurance */}
                            <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} fontSize="0.74rem">
                                ₹{fmt(b.expenses?.total)}
                              </Typography>
                              {((b.charges?.rto ?? 0) + (b.charges?.insurance ?? 0)) > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  RTO+Ins ₹{fmt((b.charges?.rto ?? 0) + (b.charges?.insurance ?? 0))}
                                </Typography>
                              )}
                            </TableCell>

                            {/* Accessory Cost */}
                            <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                              {(b.accessoryCost ?? 0) > 0 ? (
                                <Typography variant="body2" fontWeight={700} fontSize="0.74rem" color="#6a1b9a">
                                  ₹{fmt(b.accessoryCost)}
                                </Typography>
                              ) : <Typography variant="caption" color="#bbb">—</Typography>}
                            </TableCell>

                            {/* Due Remaining */}
                            <TableCell sx={{
                              fontWeight: 700, fontSize: "0.75rem",
                              color: isDue ? "#e65100" : "#999", whiteSpace: "nowrap"
                            }}>
                              {isDue ? `₹${fmt(b.due?.remainingAmount)}` : "—"}
                            </TableCell>

                            {/* Exchange Remaining */}
                            <TableCell sx={{
                              fontWeight: 700, fontSize: "0.75rem",
                              color: isExchange ? "#1565c0" : "#999", whiteSpace: "nowrap"
                            }}>
                              {isExchange ? (
                                <Box display="flex" alignItems="center" gap={0.4}>
                                  <SwapHorizIcon sx={{ fontSize: 13, color: "#1565c0" }} />
                                  ₹{fmt(b.exchange?.remainingAmount)}
                                </Box>
                              ) : "—"}
                            </TableCell>

                            {/* Exchange Net P/L — uses exchangeBikeNetPL from API */}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {exchNetPL !== 0 ? (
                                <Chip
                                  icon={exchNetPL > 0
                                    ? <TrendingUpIcon   sx={{ fontSize: 11 }} />
                                    : <TrendingDownIcon sx={{ fontSize: 11 }} />}
                                  label={`${exchNetPL > 0 ? "+" : ""}₹${fmt(exchNetPL)}`}
                                  size="small"
                                  sx={{
                                    fontWeight: 700, fontSize: "0.62rem",
                                    bgcolor: exchNetPL > 0 ? "#e8f5e9" : "#ffebee",
                                    color:   exchNetPL > 0 ? "#2e7d32" : "#c62828",
                                    border:  `1px solid ${exchNetPL > 0 ? "#a5d6a7" : "#ef9a9a"}`
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="#ccc">—</Typography>
                              )}
                            </TableCell>

                            {/* Profit / Loss */}
                            <TableCell sx={{
                              fontWeight: 800, fontSize: "0.78rem",
                              color: isProfit ? "#2e7d32" : "#c62828", whiteSpace: "nowrap"
                            }}>
                              {isProfit ? "+" : ""}₹{fmt(Math.abs(b.profitLoss ?? 0))}
                            </TableCell>

                            {/* P&L Status chip */}
                            <TableCell>
                              <Chip
                                icon={isProfit
                                  ? <TrendingUpIcon   sx={{ fontSize: 12 }} />
                                  : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                                label={b.status} size="small"
                                sx={{
                                  fontWeight: 700, fontSize: "0.65rem",
                                  bgcolor: isProfit ? "#e8f5e9" : "#ffebee",
                                  color:   isProfit ? "#2e7d32" : "#c62828",
                                  border:  `1px solid ${isProfit ? "#a5d6a7" : "#ef9a9a"}`
                                }}
                              />
                            </TableCell>

                            {/* Due Status chip */}
                            <TableCell>
                              <Chip
                                icon={isDue
                                  ? <WarningAmberIcon sx={{ fontSize: 12 }} />
                                  : <CheckCircleIcon  sx={{ fontSize: 12 }} />}
                                label={b.due?.status || "PAID"} size="small"
                                sx={{
                                  fontWeight: 700, fontSize: "0.65rem",
                                  bgcolor: isDue ? "#fff3e0" : "#e8f5e9",
                                  color:   isDue ? "#e65100" : "#2e7d32",
                                  border:  `1px solid ${isDue ? "#ffb74d" : "#a5d6a7"}`
                                }}
                              />
                            </TableCell>

                            {/* Actions */}
                            <TableCell>
                              <Box display="flex" gap={0.75} flexWrap="nowrap">
                                <Button size="small" variant="outlined"
                                  onClick={() => handleView(b.saleId)}
                                  sx={{ fontSize: "0.68rem", fontWeight: 700, borderRadius: 1.5, px: 1, py: 0.3, minWidth: 0 }}>
                                  View
                                </Button>
                                {isDue && (
                                  <Button size="small" variant="contained"
                                    startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                    onClick={() => openDueModal(b)}
                                    sx={{
                                      fontSize: "0.68rem", fontWeight: 700, borderRadius: 1.5,
                                      px: 1, py: 0.3, minWidth: 0,
                                      bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" }
                                    }}>
                                    Due
                                  </Button>
                                )}
                              </Box>
                            </TableCell>

                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Collapse>

          </Box>
        );
      })}

      {/* ══════════════════════════════════════════════════════════════
          ✅ DETAIL MODAL — imported from EvProfitLossDetailModal.jsx
      ══════════════════════════════════════════════════════════════ */}
      <EvProfitLossDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        detailData={detailData}
        detailLoading={detailLoading}
      />

      {/* ── DUE PAYMENT MODAL ── */}
      <Dialog
        open={dueOpen}
        onClose={() => !dueSubmitting && setDueOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          fontWeight: 800, bgcolor: "#e65100", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon /> Add Installment
          </Box>
          <IconButton onClick={() => setDueOpen(false)} disabled={dueSubmitting}
            sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 3 }}>
          {/* Sale summary */}
          <Paper elevation={0} sx={{
            p: 1.5, mb: 2.5, borderRadius: 2,
            bgcolor: "#fff8f5", border: "1px solid #ffb74d"
          }}>
            <Typography variant="body2" fontWeight={700} color="#e65100" mb={0.5}>
              {dueSale?.customerName} — {dueSale?.invoiceNo}
            </Typography>
            {/* ✅ vehicleType chip in due modal */}
            {dueSale?.vehicleType && (
              <Chip label={dueSale.vehicleType.replace("_", " ")} size="small"
                sx={{ mb: 0.8, bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700, fontSize: "0.65rem" }} />
            )}
            <Box display="flex" gap={3}>
              <Box><Label>Total Due</Label><Val>₹{fmt(dueSale?.due?.totalAmount)}</Val></Box>
              <Box><Label>Paid</Label><Val color="#2e7d32">₹{fmt(dueSale?.due?.paidAmount)}</Val></Box>
              <Box><Label>Remaining</Label><Val color="#e65100">₹{fmt(dueSale?.due?.remainingAmount)}</Val></Box>
            </Box>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Payment Amount *" type="number"
                value={dueForm.amount}
                onChange={(e) => setDueForm((p) => ({ ...p, amount: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyRupeeIcon sx={{ fontSize: 14 }} />
                    </InputAdornment>
                  )
                }}
                helperText={`Max: ₹${fmt(dueSale?.due?.remainingAmount)}`}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Payment Date *" type="date"
                value={dueForm.date}
                onChange={(e) => setDueForm((p) => ({ ...p, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthIcon sx={{ fontSize: 14 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Note (optional)" multiline rows={1}
                value={dueForm.note}
                onChange={(e) => setDueForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Cash — 2nd instalment"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
            {/* ✅ remark field — evSaleSchema due.payments.remark */}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Remark (optional)" multiline rows={1}
                value={dueForm.remark}
                onChange={(e) => setDueForm((p) => ({ ...p, remark: e.target.value }))}
                placeholder="e.g. Bank transfer"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
          </Grid>

          {dueError   && <Alert severity="error"   sx={{ mt: 1.5, borderRadius: 2 }}>{dueError}</Alert>}
          {dueSuccess && <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>{dueSuccess}</Alert>}

          <Button fullWidth variant="contained" onClick={handleDueSubmit} disabled={dueSubmitting}
            sx={{
              mt: 2.5, py: 1.2, borderRadius: 2, fontWeight: 800, fontSize: "0.9rem",
              bgcolor: dueSubmitting ? "#bdbdbd" : "#e65100",
              "&:hover": { bgcolor: "#bf360c" }
            }}>
            {dueSubmitting ? "Processing..." : "✅ Confirm Payment"}
          </Button>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default ProfitLossDashboard;